import type { ReactNode } from "react";

interface CommonLinkProps {
  children: ReactNode;
}

interface NavigationLinkProps extends CommonLinkProps {
  href: string;
  /** Open in a new tab with safe rel attributes. */
  external?: boolean;
  onClick?: never;
}

interface ActionLinkProps extends CommonLinkProps {
  onClick: () => void;
  href?: never;
  external?: never;
}

type LinkProps = NavigationLinkProps | ActionLinkProps;

/**
 * Inline text link — brand color with a hover underline. Renders inline and
 * inherits the surrounding type, so it sits naturally in a sentence and wraps
 * with it. The color lives on the element itself; don't reach for `T` to tint
 * a link.
 *
 * Pass `href` to navigate, or `onClick` for an in-page action (which renders a
 * `<button>`, so it stays keyboard-reachable and screen readers announce an
 * action rather than a destination). Exactly one of the two.
 *
 * For a standalone action rather than one inside a sentence, use
 * `<Button size="link">` — it's bold and sized on its own terms.
 */
export function Link(props: LinkProps) {
  // `brand-text`, not `brand`: the fill colour is the same #4c62ea in both
  // themes, which nearly disappears as type on the dark page.
  const className = "cursor-pointer text-brand-text hover:underline";

  if (props.onClick !== undefined) {
    // `text-left` and the inherited font: a <button> centers its text and
    // resets to the browser UI font, neither of which a run of inline prose
    // wants.
    return (
      <button
        type="button"
        onClick={props.onClick}
        className={`${className} inline text-left font-[inherit]`}
      >
        {props.children}
      </button>
    );
  }

  return (
    <a
      href={props.href}
      className={className}
      {...(props.external
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
    >
      {props.children}
    </a>
  );
}
